/**
 * Base API Client
 * Handles all HTTP requests to the backend API
 */

import { getRuntimeConfig } from '@/libs/runtime-config'

export interface ApiResponse<T = any> {
  data?: T
  message?: string
  error?: string | string[]
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: any
  ) {
    super(statusText)
    this.name = 'ApiError'
  }
}

/** Keys we clear on 401 (session expired) */
const AUTH_STORAGE_KEYS = [
  'backend_jwt_token',
  'next-auth.session-token',
  'next-auth.csrf-token',
  '__Secure-next-auth.session-token',
  '__Secure-next-auth.csrf-token',
]

/** Endpoints that return 401 for invalid credentials; we do not treat as "session expired" or redirect. */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/forgot-password']

function isAuthEndpoint(endpoint: string): boolean {
  const path = endpoint.split('?')[0]
  return AUTH_ENDPOINTS.some((p) => path.endsWith(p) || path.includes(p + '/'))
}

/**
 * Handle 401 Unauthorized: clear token, clear auth-related storage, sign out NextAuth, redirect to login.
 * Called when backend returns 401 (e.g. session expired after a day).
 * Skipped for login/register so failed login does not trigger redirect.
 */
function handleUnauthorized(endpoint: string): void {
  if (isAuthEndpoint(endpoint)) return
  if (globalThis.window === undefined) return

  const client = (globalThis as any).__apiClientRef as ApiClient | undefined
  if (client) {
    client.clearToken()
  }

  try {
    localStorage.removeItem('backend_jwt_token')
    sessionStorage.removeItem('backend_jwt_token')
    for (const key of AUTH_STORAGE_KEYS) {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    }
  } catch (e) {
    console.warn('[API Client] Failed to clear some storage:', e)
  }

  import('next-auth/react').then(({ signOut }) => {
    signOut({ redirect: false }).finally(() => {
      const pathname = globalThis.location.pathname
      const locale = pathname.startsWith('/ar') ? 'ar' : 'en'
      globalThis.location.href = `/${locale}/login`
    })
  })
}

class ApiClient {
  private readonly baseURL: string | null = null
  private token: string | null = null

  constructor(baseURL?: string) {
    // Allow explicit baseURL override, otherwise will be resolved dynamically
    if (baseURL) {
      this.baseURL = baseURL
    }

    // Try to get token from localStorage as fallback (set during login)
    if (globalThis.window) {
      this.token = localStorage.getItem('backend_jwt_token')
    }
  }

  /**
   * Get the base URL dynamically from runtime config
   * This ensures we always get the latest value from globalThis.__RUNTIME_CONFIG__
   */
  private getBaseURL(): string {
    // If explicitly set, use it
    if (this.baseURL) {
      return this.baseURL
    }

    // Otherwise, get from runtime config (supports globalThis.__RUNTIME_CONFIG__)
    // Default value ensures we always return a string
    return getRuntimeConfig('NEXT_PUBLIC_API_URL', 'http://localhost:5000') || 'http://localhost:5000'
  }

  /**
   * Set the backend JWT token (called after login)
   */
  setToken(token: string | null) {
    this.token = token
    if (globalThis.window) {
      if (token) {
        localStorage.setItem('backend_jwt_token', token)
      } else {
        localStorage.removeItem('backend_jwt_token')
      }
    }
  }

  /**
   * Clear the token (called on logout)
   */
  clearToken() {
    this.setToken(null)
  }

  /**
   * Get the backend JWT token from various sources (synchronous first, then async)
   */
  private getTokenSync(): string | null {
    // First, try to get from instance variable (fastest)
    if (this.token) {
      return this.token
    }

    // If in browser, try synchronous storage first
    if (globalThis.window) {
      // 1. Try localStorage (set by BackendTokenManager)
      const localToken = localStorage.getItem('backend_jwt_token')
      if (localToken) {
        this.token = localToken
        return localToken
      }

      // 2. Try sessionStorage (alternative storage)
      const sessionToken = sessionStorage.getItem('backend_jwt_token')
      if (sessionToken) {
        this.token = sessionToken
        return sessionToken
      }
    }

    return null
  }

  /**
   * Get the backend JWT token from NextAuth session (async fallback)
   */
  private async getTokenFromSession(): Promise<string | null> {
    try {
      const { getSession } = await import('next-auth/react')
      const session = await getSession()
      if (session && (session as any).backend_access_token) {
        const token = (session as any).backend_access_token
        // Store it for future use
        this.token = token
        if (globalThis.window) {
          localStorage.setItem('backend_jwt_token', token)
          sessionStorage.setItem('backend_jwt_token', token)
        }
        return token
      }
    } catch (err) {
      // NextAuth not available or session not loaded yet
      console.debug('[API Client] Could not get token from NextAuth session:', err)
    }
    return null
  }

  /**
   * Get authorization headers
   */
  private async getHeaders(includeAuth: boolean = true): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // Add Authorization header if token is available
    if (includeAuth) {
      // Try synchronous sources first (fast)
      let token = this.getTokenSync()

      // If no token found, try async NextAuth session
      if (!token) {
        token = await this.getTokenFromSession()
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`

        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
          // console.log('[API Client] Authorization header added:', {
          //   hasToken: !!token,
          //   tokenLength: token?.length,
          //   tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
          // })
        }
      } else {
        // Debug logging when no token found
        if (process.env.NODE_ENV === 'development') {
          // console.warn('[API Client] No token found for request. Sources checked:', {
          //   instanceToken: !!this.token,
          //   localStorage: globalThis.window ? !!localStorage.getItem('backend_jwt_token') : 'N/A',
          //   sessionStorage: globalThis.window ? !!sessionStorage.getItem('backend_jwt_token') : 'N/A'
          // })
        }
      }
    }

    return headers
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response, endpoint: string): Promise<T> {
    if (response.status === 401) {
      handleUnauthorized(endpoint)
      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')
      const data = isJson ? await response.json().catch(() => ({})) : await response.text()
      throw new ApiError(response.status, response.statusText, data)
    }

    const contentType = response.headers.get('content-type')
    const isJson = contentType?.includes('application/json')

    let data: any
    if (isJson) {
      data = await response.json()
    } else {
      data = await response.text()
    }
    if (!response.ok) {
      throw new ApiError(
        response.status,
        response.statusText,
        data
      )
    }

    return data
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, includeAuth: boolean = true): Promise<T> {
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include', // Important for cookies
    })

    return this.handleResponse<T>(response, endpoint)
  }

  /**
   * GET request that returns response as Blob (e.g. file export).
   */
  async getBlob(endpoint: string, includeAuth: boolean = true): Promise<Blob> {
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })
    if (response.status === 401) {
      handleUnauthorized(endpoint)
      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')
      const data = isJson ? await response.json().catch(() => ({})) : await response.text()
      throw new ApiError(response.status, response.statusText, data)
    }
    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')
      const data = isJson ? await response.json() : await response.text()
      throw new ApiError(response.status, response.statusText, data)
    }
    return response.blob()
  }

  /**
   * POST request
   * When data is FormData, Content-Type is omitted so fetch sets multipart/form-data with boundary.
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    includeAuth: boolean = true
  ): Promise<T> {
    const isFormData = data instanceof FormData
    const headers = await this.getHeaders(includeAuth)

    if (isFormData) {
      // Do not set Content-Type for FormData; fetch will set multipart/form-data with boundary
      const { 'Content-Type': _ct, ...rest } = headers as Record<string, string>
      const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
        method: 'POST',
        headers: rest,
        credentials: 'include',
        body: data,
      })
      return this.handleResponse<T>(response, endpoint)
    }

    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'POST',
      headers,
      credentials: 'include', // Important for cookies
      body: data ? JSON.stringify(data) : undefined,
    })

    return this.handleResponse<T>(response, endpoint)
  }

  /**
   * PUT request
   */
  async put<T = any>(
    endpoint: string,
    data?: any,
    includeAuth: boolean = true
  ): Promise<T> {
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    })

    return this.handleResponse<T>(response, endpoint)
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    endpoint: string,
    includeAuth: boolean = true
  ): Promise<T> {
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    })

    return this.handleResponse<T>(response, endpoint)
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    endpoint: string,
    data?: any,
    includeAuth: boolean = true
  ): Promise<T> {
    const headers = await this.getHeaders(includeAuth)
    const response = await fetch(`${this.getBaseURL()}${endpoint}`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    })

    return this.handleResponse<T>(response, endpoint)
  }
}

// Export singleton instance (baseURL will be resolved dynamically from runtime config)
export const apiClient = new ApiClient()
if (typeof globalThis !== 'undefined') {
  (globalThis as any).__apiClientRef = apiClient
}

// Export methods for external use
export const setApiToken = (token: string | null) => apiClient.setToken(token)
export const clearApiToken = () => apiClient.clearToken()

